// src/App.js

import React from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { ConfigProvider } from "antd";
import {
  BarElement,
  CategoryScale,
  TimeScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Tooltip as ChartJSTooltip,
} from "chart.js";
import "chartjs-adapter-dayjs-3";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";
import { ForgotPassword } from "./pages/forgotPassword";
import { ResetPassword } from "./pages/resetPassword";
import { Admin } from "./pages/admin";
import { Page_404 } from "./pages/404";
import ProtectedRoute from "./components/ProtectedRoute";
import DefaultRedirect from "./components/DefaultRedirect";

import "./pages/overrides.css";
import { useUser } from "./context/UserContext";
import { COLORS } from "./consts";
import ROUTES from "./consts/routes";
import SentryRouteTracker from "./SentryRouteTracker.jsx";

ChartJS.register(
  TimeScale,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  ChartJSTooltip,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin,
  annotationPlugin,
);

ChartJS.defaults.plugins.tooltip.backgroundColor = "white";
ChartJS.defaults.plugins.tooltip.titleColor = "black";
ChartJS.defaults.plugins.tooltip.bodyColor = "black";
ChartJS.defaults.plugins.tooltip.borderColor = "white";
ChartJS.defaults.plugins.tooltip.borderWidth = 1;
ChartJS.defaults.plugins.tooltip.titleFont = {
  family: "'museoSans', sans-serif",
};
ChartJS.defaults.plugins.tooltip.bodyFont = {
  family: "'museoSans', sans-serif",
};
ChartJS.defaults.plugins.tooltip.padding = 12;

function App() {
  const { isAuthenticated } = useUser();

  return (
    <ConfigProvider theme={{ token: { colorPrimary: COLORS.primaryBlue } }}>
      <Router>
        <SentryRouteTracker />
        <Routes>
          {/* Root Route uses DefaultRedirect */}
          <Route path="/" element={<DefaultRedirect />} />

          <Route
            path="/forgot_password"
            element={
              !isAuthenticated() ? <ForgotPassword /> : <DefaultRedirect />
            }
          />
          <Route
            path="/reset_password"
            element={
              !isAuthenticated() ? <ResetPassword /> : <DefaultRedirect />
            }
          />

          {/* Protected Routes */}
          {ROUTES.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ProtectedRoute
                  component={route.component}
                  roles={route.roles}
                  orgTypes={route.orgTypes}
                  envAccess={route.envAccess}
                  componentType={route.componentType}
                  requiredPermissions={route.requiredPermissions}
                  superuserOnly={route.superuserOnly}
                />
              }
            />
          ))}

          {/* Admin Route Example */}
          <Route
            path="/admin"
            element={<ProtectedRoute roles={["admin"]} component={Admin} />}
          />

          {/* 404 Route */}
          <Route path="*" element={<Page_404 />} />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;
